import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useLazyQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { useFormik } from 'formik';
import get from 'lodash.get';
import * as yup from 'yup';
import { Link, Modal } from '@mui/material';
import {
  AuthTextStyle,
  LoginContainerStyle,
  LoginFormStyle,
  ModalStyle,
  NotificationContainerStyle,
  MigrationNotificationStyle,
  StyledButton,
  StyledH1,
  StyledH2,
  RerouteButtonStyle
} from './Login.styles';
import { INITIATE_PARTNER_AUTHENTICATION, HAS_PROPERTY_MIGRATED } from './Login.gql';
import { colorBlue, colorCobalt } from '../../layout/css/colors';
import PropertySelectionModal from '../../buildingBlocks/propertySelection/PropertySelectionModal';
import reducer from '../../reducer/state.reducer';
import withAnalytics from '../../hoc/withAnalytics';

const getProperties = (data) => get(data, 'PartnerHubMutations.initiatePartnerAuthentication.properties');

const renderButtonContent = ({ loading, multiPropertyGenLoading, isSent, error }) => {
  if (loading || multiPropertyGenLoading) return <CircularProgress />;
  if (isSent && error) return `Re-send authorization email`;
  return `Verify email`;
};

const Login = (props) => {
  const { analytics } = props;

  const initialState = {
    sentToEmailAddress: '',
    isSent: false,
    whyModal: false,
    propertySelectionValues: null,
    multiPropertyGen: { error: '', data: '', loading: '' },
    hasMigrated: false
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleSetState = (newValue) => {
    dispatch({
      type: 'SET_STATE',
      newValue
    });
  };

  const schema = yup.object().shape({
    emailAddress: yup.string().email('Invalid Email format.').required('Email address is required')
  });

  const resetIsSent = () => {
    handleSetState({
      isSent: false,
      multiPropertyGen: { error: '', data: '', loading: '' }
    });
  };

  const handleRerouteToNewSite = () => {
    window.open(
      process.env.REACT_APP_LODGELINK_NEW_PARTNER_HUB_LINK ?? 'http://localhost:8004',
      '_blank',
      'noopener,noreferrer'
    );
  };

  const formik = useFormik({
    initialValues: {
      emailAddress: ''
    },
    onSubmit: async (values) => {
      try {
        const { emailAddress } = values;
        resetIsSent();
        // eslint-disable-next-line no-use-before-define
        await initiatePartnerAuthentication({
          variables: { emailAddress }
        });
      } catch (onSubmitError) {
        handleSetState({
          multiPropertyGen: { error: 'Please try with a different Email Address.', data: '', loading: '' }
        });
      } finally {
        formik.resetForm();
      }
    },
    validationSchema: schema,
    validateOnChange: false
  });

  const [checkMigration, { loading: hasPropertyMigratedLoading }] = useLazyQuery(HAS_PROPERTY_MIGRATED);

  const handleMigrationLogin = async (email) => {
    const { data } = await checkMigration({ variables: { emailAddress: email } });
    return data?.PartnerHubQueries.hasPropertyMigrated;
  };

  //  Initialize the mutation to initiate partner authentication and handle the response
  const [initiatePartnerAuthentication, { error, data, loading }] = useMutation(INITIATE_PARTNER_AUTHENTICATION, {
    onCompleted: (onCompletedData) => {
      const properties = getProperties(onCompletedData);
      if (properties.length)
        handleSetState({
          propertySelectionValues: {
            emailAddress: formik.values.emailAddress,
            properties
          }
        });
    }
  });

  const isLoading = loading || hasPropertyMigratedLoading;

  // On submit, determine if the property has been migrated
  const handleSubmitEmail = async () => {
    try {
      const handleMigrationRes = await handleMigrationLogin(formik.values.emailAddress);

      // If handleMigrationLogin returns undefined, the email was not found in the database
      if (handleMigrationRes === undefined) {
        throw TypeError('Please try with a different Email Address.');
      }

      // Save the state globally
      handleSetState({ hasMigrated: handleMigrationRes });

      // If the property hasn't been migrated yet, continue with 2.0 site
      if (!handleMigrationRes) {
        analytics.trackGA4('track-event', {
          eventName: 'emailVerificationRequested'
        });
        handleSetState({ sentToEmailAddress: formik.values.emailAddress });
        formik.handleSubmit(formik.values, initiatePartnerAuthentication);
      }

      // Clear any errors

      handleSetState({ multiPropertyGen: { error: '', data: '', loading: '' } });
    } catch (err) {
      if (err instanceof TypeError) {
        handleSetState({
          multiPropertyGen: {
            error: err.message,
            data: '',
            loading: ''
          }
        });
      } else {
        // Send a general error to display if something else went wrong
        handleSetState({
          multiPropertyGen: {
            error: 'An error has occurred while trying to access the service. Please contact support',
            data: '',
            loading: ''
          }
        });
      }
    }
  };

  const { sentToEmailAddress, isSent, whyModal, propertySelectionValues, multiPropertyGen, hasMigrated } = state;

  useEffect(() => {
    handleSetState({
      isSent: !!error || (!!data && !getProperties(data).length) || !!multiPropertyGen.error || !!multiPropertyGen.data
    });
  }, [error, data, multiPropertyGen]);

  // If the user changes the email address, reset the notification state
  useEffect(() => {
    if (hasMigrated) {
      handleSetState({ hasMigrated: false });
    }
    if (isSent) {
      handleSetState({ isSent: false });
    }
  }, [formik.values.emailAddress]);

  const renderDialog = () => {
    if (formik.errors.emailAddress) return <StyledH1 color="red">{formik.errors.emailAddress}</StyledH1>;
    // Display error message if state contains an error message
    if (error || multiPropertyGen.error)
      return (
        <>
          <StyledH1>Email Address could not be verified.</StyledH1>
          <StyledH1>{multiPropertyGen.error}</StyledH1>
        </>
      );

    if (hasMigrated)
      return (
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            alignItems: 'center',
            gap: '20px'
          }}
        >
          <Box sx={MigrationNotificationStyle}>
            <span style={{ p: 3 }}>
              <span
                className="top-notif"
                style={{ display: 'flex', flexDirection: 'row', padding: '0 3px', gap: '5px' }}
              >
                <span className="button-frame" style={{ paddingTop: '9px' }}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: colorCobalt }} />
                </span>
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                    padding: '7px',
                    gap: '3px'
                  }}
                >
                  <Box style={{ fontWeight: 500, fontSize: '18px' }}>
                    Good news - your account has moved to the new PartnerHub
                  </Box>
                  <span>
                    Everything you need is now there. Sign in at the new address and pick up right where you left off.
                  </span>
                </span>
              </span>
              <span
                className="bottom-notif"
                style={{ display: 'flex', flexDirection: 'row', padding: '0 3px', gap: '5px' }}
              >
                <span className="button-frame" style={{ visibility: 'hidden' }}>
                  <InfoOutlinedIcon fontSize="small" />
                </span>
                <span style={{ padding: '7px' }}>
                  <Box type="submit" onClick={handleRerouteToNewSite} sx={RerouteButtonStyle}>
                    <span style={{ fontFamily: 'Avenir Next', fontWeight: 600 }}>Continue to the new PartnerHub</span>
                    <ArrowForwardIosOutlinedIcon fontSize="small" />
                  </Box>
                </span>
              </span>
            </span>
          </Box>
          <span style={{ fontFamily: 'Avenir Next' }}>Bookmark the new address — this page will retire soon.</span>
        </span>
      );

    return (
      <>
        <StyledH1 bold>Thank you!</StyledH1>
        <StyledH1>
          <span>An authorized link to your PartnerHub has been sent to&nbsp;</span>
          <span style={{ color: colorBlue, fontWeight: 'bold' }}>{sentToEmailAddress}</span>
        </StyledH1>
        <StyledH1>Not there?</StyledH1>
        <StyledH1>Make sure to check your spam folder and email address</StyledH1>
      </>
    );
  };

  return (
    <Box sx={LoginContainerStyle}>
      <Box sx={AuthTextStyle}>
        <StyledH1 size="25px">Email Authentication Required&nbsp;</StyledH1>
        <StyledH1
          size="25px"
          color={colorBlue}
          onClick={() => handleSetState({ whyModal: true })}
          style={{ cursor: 'pointer' }}
        >
          (why?)
        </StyledH1>
      </Box>
      {whyModal && (
        <Modal open onClose={() => handleSetState({ whyModal: false })}>
          <Box sx={ModalStyle}>
            <StyledH2 size="20px">
              {`By providing your front desk email registered with LodgeLink and obtaining a secure login link, 
                we’ve removed the need for yet another password to remember. 
                This also provides an extra layer of security to protect you and your organization’s financial liabilities.`}
            </StyledH2>
          </Box>
        </Modal>
      )}
      {propertySelectionValues && (
        <PropertySelectionModal values={propertySelectionValues} handleSetState={handleSetState} />
      )}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitEmail();
        }}
        sx={LoginFormStyle}
      >
        <TextField
          sx={{ width: '50ch' }}
          id="emailAddress"
          name="emailAddress"
          label="Email Address"
          variant="outlined"
          title="emailAddress"
          value={formik.values.emailAddress}
          error={!!formik.errors.emailAddress && formik.submitCount > 0}
          onChange={(e) => formik.setFieldValue('emailAddress', e.target.value)}
        />
        <StyledButton name="submit" variant="contained" type="submit" disabled={hasMigrated}>
          {renderButtonContent({
            loading: isLoading,
            multiPropertyGenLoading: multiPropertyGen.loading,
            error,
            isSent
          })}
        </StyledButton>
        {(hasMigrated || isSent || formik.errors.emailAddress) && (
          <Box sx={NotificationContainerStyle}>{renderDialog()}</Box>
        )}
        <StyledH1>
          <span>If you are looking to list your property with LodgeLink&nbsp;</span>
          <span>
            <Link style={{ color: colorBlue }} href="https://www.lodgelink.com/becoming-a-supplier/" target="_blank">
              click here to contact us
            </Link>
          </span>
        </StyledH1>
      </Box>
    </Box>
  );
};

Login.propTypes = {
  analytics: PropTypes.shape({ trackGA4: PropTypes.shape({}) })
};

Login.defaultProps = {
  analytics: {}
};

export default withAnalytics()(Login);
